import { useEffect, useRef, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useDispatch } from "react-redux";

/** Lib Imports */
import { Button } from "primereact/button";
import { Checkbox } from "primereact/checkbox";
import { Password } from "primereact/password";
import { InputText } from "primereact/inputtext";
import MolForm, { Field } from "@moltech/molui/molform";
import Toast from "@moltech/molui/toast";


/** Utilities */
import { authentication } from "../../app/slice/authSlice";
import {
  createCookie,
  getCookie,
  isCookieExist,
  removeCookie,
} from "../../utils/cookies/CookieHandler";
import {
  consumePostLoginRedirect,
  getCurrentAppPath,
  isAuthPagePath,
} from "../../utils/authentication/authLibrary";
import {
  validateEmail,
} from "../../utils/validators/FieldsValidators.js";
import { encryptUrlData } from "../../utils/encryption/CryptoService.js";
import { PreloadDropdownData } from "../../utils/common/PreloadDropdownData"

/** RTK Query */
import { useLoginMutation } from "../../app/services/authApi.js";

/** CSS */
import "./Login.scss";

const initialValue = {
  email: "",
  password: "",
  rememberMe: false,
};

const saveRememberMeCookie = (formData) => {
  createCookie({
    cookieName: "rememberMe",
    cookieValue: {
      email: formData.email,
      password: formData.password,
    },
    path: "/",
    expirationTime: 60,
  });
};

const handleRememberMe = (rememberMe, formData) => {
  if (rememberMe) {
    saveRememberMeCookie(formData);
    return;
  }

  if (isCookieExist("rememberMe")) {
    removeCookie("rememberMe");
  }
};

const navigateAfterLogin = (loginData, navigate) => {
  const encryptedEmail = encodeURIComponent(
    encryptUrlData(loginData.user.email)
  );

  if (loginData.user?.isResetPasswordRequired) {
    navigate(`/reset-password/${encryptedEmail}`);
    return;
  }

  const saved = consumePostLoginRedirect();
  const current = getCurrentAppPath();
  const fallback =
    current && !isAuthPagePath(current) ? current : "/";

  navigate(saved || fallback);
};

const Login = () => {
  const loginFormRef = useRef();
  const navigate = useNavigate();
  const dispatch = useDispatch();

  const [formValues, setFormValues] = useState(initialValue);
  const [rememberMe, setRememberMe] = useState(formValues.rememberMe);

  const [
    login,
    { isLoading: isLoginLoading, isSuccess: isLoginSuccess, data: loginData },
  ] = useLoginMutation();

  useEffect(() => {
    if (isCookieExist("rememberMe")) {
      const rememberMeCookie = getCookie("rememberMe");
      const cookieData = {
        email: rememberMeCookie.email,
        password: rememberMeCookie.password,
        rememberMe: true,
      };
      setFormValues({ ...cookieData });
    } else {
      resetForm();
    }
  }, []);

  useEffect(() => {
    if (!isLoginSuccess || !loginData?.isAuthenticated) return;

    dispatch(authentication(loginData));

    if (!loginData.user?.isResetPasswordRequired) {
      PreloadDropdownData(dispatch);
    }

    const formData = loginFormRef.current?.getData();
    handleRememberMe(rememberMe, formData);

    navigateAfterLogin(loginData, navigate);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isLoginSuccess, loginData]);

  const resetForm = () => {
    setFormValues(initialValue);
  };

  const handleFormSubmit = () => {
    const formData = loginFormRef.current?.getData();
    if (formData) {
      const formReq = {
        email: formData.email,
        password: formData.password,
      };
      setRememberMe(formData.rememberMe);
      login(formReq).then((res) => {
        if (res.error) {
          const status = res.error.status;
          const message = res.error.data?.message;
          if (status === 401) {
            Toast.error(message);
          }
        }
      });
    }
  };

  const handleSaveClick = () => {
    if (loginFormRef.current) {
      loginFormRef.current.submit();
    }
  };

  const handleKeyDown = (e) => {
    if (e.key === 'Enter') {
      e.preventDefault();
      handleSaveClick();
    }
  };

  return (
    <div className="login-page-wrapper">
      <div className="login-page-card">
        {/* Left Form Panel */}
        <div className="login-form">
          <div className="login-card">
            <div className="brand-header">
              <img className="big-logo" src="/images/logo.png" alt="Moltech logo" />
              <span className="app-portal-badge">
                <i className="pi pi-shield"></i> Portal
              </span>
            </div>

            <div className="welcome-header">
              <h2>Welcome to MoltechR</h2>
              <p className="sub-text">Log in to your account to continue.</p>
            </div>

            <div className="saporate-line"></div>

            <div className="form-container">
              <MolForm
                ref={loginFormRef}
                onSubmit={handleFormSubmit}
                initialValues={formValues}
              >
                <div className="form-field">
                  <Field
                    component={InputText}
                    name="email"
                    label="Email Address"
                    placeholder="name@company.com"
                    validation={validateEmail}
                    onKeyDown={handleKeyDown}
                  />
                </div>
                <div className="form-field">
                  <Field
                    component={Password}
                    feedback={false}
                    toggleMask
                    name="password"
                    label="Password"
                    placeholder="Enter your password"
                    validation="required"
                    onKeyDown={handleKeyDown}
                  />
                </div>
                <div className="form-options-row d-flex justify-content-between align-items-center">
                  <div className="login-checkbox">
                    <Field
                      className="checkbox-field"
                      component={Checkbox}
                      name="rememberMe"
                      label="Remember Me"
                    />
                  </div>
                  <div className="forgot-pass-sec">
                    <Link to={"/forgot-password"} tabIndex={-1}>
                      Forgot Password?
                    </Link>
                  </div>
                </div>
              </MolForm>

              <Button
                label="Login"
                icon={isLoginLoading ? undefined : "pi pi-sign-in"}
                loading={isLoginLoading}
                className="btn primary-btn login-btn"
                onClick={handleSaveClick}
              />
            </div>

            <div className="bottom-text">
              <i className="pi pi-lock lock-icon"></i>
              <span>
                Streamline Your Hiring Process with MoltechR. Manage job applications,
                organize interview workflows, and track candidate progress all in one centralized platform.
              </span>
            </div>
          </div>
        </div>

        {/* Right Showcase Panel */}
        <div className="login-image">
          {/* Animated Background Mesh & Ambient Glows */}
          <div className="showcase-bg-grid"></div>
          <div className="ambient-glow glow-orange"></div>
          <div className="ambient-glow glow-purple"></div>
          <div className="ambient-glow glow-bottom-orange"></div>

          {/* Scaled Showcase Frame with 1:1 image alignment */}
          <div className="showcase-art-frame">
            {/* Base Artwork Image */}
            <img
              src="/images/login-bg.png"
              alt="MoltechR Dashboard"
              className="showcase-bg-art"
            />

            {/* 1. Purple Cube Floating Icon Animation */}
            <div className="anim-marker marker-purple-cube">
              <span className="pulse-ring purple-ring"></span>
              <span className="glow-flare purple-flare"></span>
            </div>

            {/* 2. Orange Users Floating Icon Animation */}
            <div className="anim-marker marker-orange-users">
              <span className="pulse-ring orange-ring"></span>
              <span className="glow-flare orange-flare"></span>
            </div>

            {/* 3. Total Revenue Glass Card Chart & Glow Animation */}
            <div className="anim-marker marker-revenue-card">
              <span className="card-shimmer-border"></span>
              <span className="chart-pulse-dot"></span>
            </div>

            {/* 4. Orange Briefcase Floating Icon Animation */}
            <div className="anim-marker marker-orange-briefcase">
              <span className="pulse-ring orange-ring"></span>
              <span className="glow-flare orange-flare"></span>
            </div>

            {/* 5. Weekly Applicants Glass Card Pulse Animation */}
            <div className="anim-marker marker-applicants-card">
              <span className="card-shimmer-border"></span>
              <span className="bars-pulse-shimmer"></span>
            </div>

            {/* 6. Recruitment & HRMS Badge Glow Animation */}
            <div className="anim-marker marker-hrms-badge">
              <span className="badge-glow-sweep"></span>
              <span className="badge-sparkle-dot"></span>
            </div>

            {/* Background Connecting Trajectory Light Particles */}
            <div className="trajectory-particle particle-1"></div>
            <div className="trajectory-particle particle-2"></div>
            <div className="trajectory-particle particle-3"></div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Login;
